import { useNavigate, useSearch } from "@tanstack/react-router";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCan } from "@/lib/auth";

import type { KbRagSearch, KbRagTab } from "..";
import { ArticlesTab } from "../components/ArticlesTab";
import { AskTab } from "../components/AskTab";
import { HowToTab } from "../components/HowToTab";
import { OverviewTab } from "../components/OverviewTab";
import { ReleaseImpactTab } from "../components/ReleaseImpactTab";
import { ReviewDeskTab } from "../components/ReviewDeskTab";
import { RunsTab } from "../components/RunsTab";
import { SearchTab } from "../components/SearchTab";

export function KbRagPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/kb-rag" }) as KbRagSearch;
  const canWrite = useCan("kb_rag.write");
  const tab: KbRagTab = search.tab ?? "overview";

  const setSearch = (next: Partial<KbRagSearch>) =>
    navigate({
      to: "/kb-rag",
      search: (prev: KbRagSearch) => ({ ...prev, ...next }),
    });

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">KB RAG</h1>
        <p className="text-sm text-muted-foreground">
          The support knowledge base as the AI agent sees it — is the nightly
          crawl healthy, what is indexed, and what the reviewer thinks should
          change.
        </p>
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => setSearch({ tab: v as KbRagTab })}
      >
        <TabsList variant="underline">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="articles">Articles</TabsTrigger>
          <TabsTrigger value="runs">Runs</TabsTrigger>
          <TabsTrigger value="search">Search</TabsTrigger>
          <TabsTrigger value="ask">Ask</TabsTrigger>
          <TabsTrigger value="review">Article Review</TabsTrigger>
          <TabsTrigger value="impact">Release Impact</TabsTrigger>
          <TabsTrigger value="how-to">How to use</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab
            onPickCategory={(cat) => setSearch({ tab: "articles", cat })}
            onPickStatus={() => setSearch({ tab: "articles" })}
          />
        </TabsContent>
        <TabsContent value="articles" className="mt-6">
          <ArticlesTab search={search} setSearch={setSearch} />
        </TabsContent>
        <TabsContent value="runs" className="mt-6">
          <RunsTab />
        </TabsContent>
        <TabsContent value="search" className="mt-6">
          <SearchTab search={search} setSearch={setSearch} />
        </TabsContent>
        <TabsContent value="ask" className="mt-6">
          <AskTab search={search} setSearch={setSearch} />
        </TabsContent>
        <TabsContent value="review" className="mt-6">
          <ReviewDeskTab
            search={search}
            setSearch={setSearch}
            canAct={canWrite}
          />
        </TabsContent>
        <TabsContent value="impact" className="mt-6">
          <ReleaseImpactTab
            search={search}
            setSearch={setSearch}
            canAct={canWrite}
          />
        </TabsContent>
        <TabsContent value="how-to" className="mt-6">
          <HowToTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
